/**
 * Focus management shared by every "selected search items" list that sits beside
 * a taxonomy tree — the TreeView sidebar/inline lists and the results filter
 * lookup dialog.
 *
 * All of them have the same defect if left alone: removing a pill (or clearing
 * every pill) destroys the control the user just activated, so focus falls back
 * to <body> and keyboard / screen-reader users lose their place (WCAG 2.4.3
 * Focus Order). All of them also target the same DOM, so the selectors and the
 * resolve-target-then-re-assert dance live here once.
 */

// TaxonomyLeydenTree (Leyden `Tree`): every row exposes a selection checkbox and
// expandable rows also expose Leyden's toggle button. The first of either is the
// natural landing spot once a selection is gone — picking terms is what the user
// does next.
export const TREE_CONTROL_SELECTOR = '.taxonomyLeydenTree .c-els-tree__button, .taxonomyLeydenTree .taxonomyLeydenTree__checkbox input';

// Legacy inline TreeViewList renders real `treeitem` rows instead.
export const INLINE_TREE_ITEM_SELECTOR = '.treeViewList [role="treeitem"]';

/**
 * Focus the first control of the tree matched by `selector`.
 *
 * @param {string} [selector] Tree control selector; defaults to the Leyden tree.
 */
export function focusFirstTreeControl(selector = TREE_CONTROL_SELECTOR) {
  document.querySelector(selector)?.focus();
}

/**
 * Focus the tree and re-assert on the next frame.
 *
 * Used by "Clear selected", which empties the list entirely: there is no sibling
 * left to fall back to, and the list unmounts as part of the re-render the click
 * triggers, so a single synchronous focus call can be undone.
 *
 * @param {string} [treeSelector] Tree control selector; defaults to the Leyden tree.
 */
export function focusTreeAfterClear(treeSelector = TREE_CONTROL_SELECTOR) {
  const applyFocus = () => focusFirstTreeControl(treeSelector);

  applyFocus();
  requestAnimationFrame(applyFocus);
}

/**
 * Move focus off an item that is about to be removed, to the previous item (or
 * the next one when the first item is removed), falling back to the first tree
 * control when the removed item was the last one.
 *
 * The target is resolved *before* the removal so a surviving-sibling reference
 * stays valid across the re-render (React reuses the DOM node for a kept key).
 * Focus is then re-asserted on the next frame because the re-render of both the
 * selected list and the tree can otherwise drop focus to the document body.
 *
 * @param {object}  options
 * @param {string}  options.itemSelector  Selects the removable items' focusable controls, in DOM order.
 * @param {number}  options.currentIndex  DOM position of the item being removed.
 * @param {string} [options.treeSelector] Fallback tree control selector; defaults to the Leyden tree.
 */
export function focusSiblingOrTree({ itemSelector, currentIndex, treeSelector = TREE_CONTROL_SELECTOR }) {
  const items = document.querySelectorAll(itemSelector);

  let target = null;
  if (items.length > 1) {
    target = currentIndex > 0 ? items[currentIndex - 1] : items[1];
  }

  const applyFocus = () => {
    if (target && target.isConnected !== false) {
      target.focus();
      return;
    }
    focusFirstTreeControl(treeSelector);
  };

  applyFocus();
  requestAnimationFrame(applyFocus);
}
